import { supabase } from './supabaseClient';

// Generate a unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('reveel_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('reveel_visitor_id', visitorId);
  }
  return visitorId;
};

// Get session ID (new session after 30 min of inactivity)
const getSessionId = () => {
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const now = Date.now();
  let sessionData = JSON.parse(localStorage.getItem('reveel_session') || '{}');

  if (!sessionData.id || (now - sessionData.lastActivity) > SESSION_TIMEOUT) {
    sessionData = {
      id: 's_' + Math.random().toString(36).substring(2) + Date.now().toString(36),
      startTime: now,
      lastActivity: now
    };
  } else {
    sessionData.lastActivity = now;
  }

  localStorage.setItem('reveel_session', JSON.stringify(sessionData));
  return sessionData;
};

// Get location data from IP
const getLocationData = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return {
      ip: data.ip,
      city: data.city,
      region: data.region,
      country: data.country_name,
      country_code: data.country_code,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      isp: data.org
    };
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  // Detect device type
  let deviceType = 'desktop';
  if (/Mobi|Android/i.test(ua)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/i.test(ua)) {
    deviceType = 'tablet';
  }

  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

  // Detect OS
  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    deviceType,
    browser,
    os,
    platform,
    userAgent: ua,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language
  };
};

// Track page view
export const trackPageView = async (page = window.location.pathname) => {
  try {
    const visitorId = getVisitorId();
    const session = getSessionId();
    const deviceInfo = getDeviceInfo();
    const locationData = await getLocationData();

    const pageViewData = {
      visitor_id: visitorId,
      session_id: session.id,
      page,
      referrer: document.referrer || null,
      device_type: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screen_width: deviceInfo.screenWidth,
      screen_height: deviceInfo.screenHeight,
      language: deviceInfo.language,
      ip_address: locationData?.ip || null,
      city: locationData?.city || null,
      region: locationData?.region || null,
      country: locationData?.country || null,
      country_code: locationData?.country_code || null,
      timezone: locationData?.timezone || null,
      created_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('page_views')
      .insert([pageViewData]);

    if (error) {
      console.error('Error tracking page view:', error);
    }
  } catch (error) {
    console.error('Error in trackPageView:', error);
  }
};

// Track session start
export const trackSessionStart = async () => {
  try {
    const visitorId = getVisitorId();
    const session = getSessionId();
    const deviceInfo = getDeviceInfo();
    const locationData = await getLocationData();

    // Check if this session already exists
    const { data: existingSession } = await supabase
      .from('sessions')
      .select('id')
      .eq('session_id', session.id)
      .single();

    if (existingSession) return; // Session already tracked

    const sessionData = {
      visitor_id: visitorId,
      session_id: session.id,
      started_at: new Date(session.startTime).toISOString(),
      device_type: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screen_width: deviceInfo.screenWidth,
      screen_height: deviceInfo.screenHeight,
      language: deviceInfo.language,
      ip_address: locationData?.ip || null,
      city: locationData?.city || null,
      region: locationData?.region || null,
      country: locationData?.country || null,
      country_code: locationData?.country_code || null,
      latitude: locationData?.latitude || null,
      longitude: locationData?.longitude || null,
      timezone: locationData?.timezone || null,
      referrer: document.referrer || null,
      landing_page: window.location.pathname
    };

    const { error } = await supabase
      .from('sessions')
      .insert([sessionData]);

    if (error) {
      console.error('Error tracking session:', error);
    }
  } catch (error) {
    console.error('Error in trackSessionStart:', error);
  }
};

// Update session duration on page unload
export const updateSessionDuration = async () => {
  try {
    const session = JSON.parse(localStorage.getItem('reveel_session') || '{}');
    if (!session.id) return;

    const duration = Math.round((Date.now() - session.startTime) / 1000); // in seconds

    await supabase
      .from('sessions')
      .update({
        duration_seconds: duration,
        ended_at: new Date().toISOString(),
        pages_viewed: await getPageCount(session.id)
      })
      .eq('session_id', session.id);
  } catch (error) {
    console.error('Error updating session duration:', error);
  }
};

// Get page count for session
const getPageCount = async (sessionId) => {
  try {
    const { count } = await supabase
      .from('page_views')
      .select('*', { count: 'exact', head: true })
      .eq('session_id', sessionId);
    return count || 1;
  } catch {
    return 1;
  }
};

// Analytics data fetching functions for Admin Panel
export const getAnalyticsOverview = async (days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  try {
    // Total page views
    const { count: totalPageViews } = await supabase
      .from('page_views')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', startDate.toISOString());

    // Total sessions
    const { count: totalSessions } = await supabase
      .from('sessions')
      .select('*', { count: 'exact', head: true })
      .gte('started_at', startDate.toISOString());

    // Unique visitors
    const { data: uniqueVisitors } = await supabase
      .from('sessions')
      .select('visitor_id')
      .gte('started_at', startDate.toISOString());

    const uniqueVisitorCount = new Set(uniqueVisitors?.map(v => v.visitor_id)).size;

    // Average session duration
    const { data: sessionDurations } = await supabase
      .from('sessions')
      .select('duration_seconds')
      .gte('started_at', startDate.toISOString())
      .not('duration_seconds', 'is', null);

    const avgDuration = sessionDurations?.length
      ? sessionDurations.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) / sessionDurations.length
      : 0;

    return {
      totalPageViews: totalPageViews || 0,
      totalSessions: totalSessions || 0,
      uniqueVisitors: uniqueVisitorCount,
      avgSessionDuration: Math.round(avgDuration)
    };
  } catch (error) {
    console.error('Error fetching analytics overview:', error);
    return null;
  }
};

export const getRecentSessions = async (limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching recent sessions:', error);
    return [];
  }
};

export const getPageViewsByPage = async (days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  try {
    const { data, error } = await supabase
      .from('page_views')
      .select('page')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Count views per page
    const pageCounts = {};
    data?.forEach(view => {
      pageCounts[view.page] = (pageCounts[view.page] || 0) + 1;
    });

    return Object.entries(pageCounts)
      .map(([page, views]) => ({ page, views }))
      .sort((a, b) => b.views - a.views);
  } catch (error) {
    console.error('Error fetching page views:', error);
    return [];
  }
};

export const getVisitorsByCountry = async (days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('country, country_code')
      .gte('started_at', startDate.toISOString())
      .not('country', 'is', null);

    if (error) throw error;

    // Count by country
    const countryCounts = {};
    data?.forEach(session => {
      const key = session.country || 'Unknown';
      if (!countryCounts[key]) {
        countryCounts[key] = { country: key, code: session.country_code, count: 0 };
      }
      countryCounts[key].count++;
    });

    return Object.values(countryCounts).sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error fetching visitors by country:', error);
    return [];
  }
};

export const getVisitorsByDevice = async (days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('device_type, browser, os')
      .gte('started_at', startDate.toISOString());

    if (error) throw error;

    // Count by device type
    const deviceCounts = {};
    const browserCounts = {};
    const osCounts = {};

    data?.forEach(session => {
      deviceCounts[session.device_type] = (deviceCounts[session.device_type] || 0) + 1;
      browserCounts[session.browser] = (browserCounts[session.browser] || 0) + 1;
      osCounts[session.os] = (osCounts[session.os] || 0) + 1;
    });

    return {
      devices: Object.entries(deviceCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
      browsers: Object.entries(browserCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
      os: Object.entries(osCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count)
    };
  } catch (error) {
    console.error('Error fetching device stats:', error);
    return { devices: [], browsers: [], os: [] };
  }
};

export const getViewsByDay = async (days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  try {
    const { data, error } = await supabase
      .from('page_views')
      .select('created_at')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Group by day
    const dayCounts = {};
    data?.forEach(view => {
      const day = view.created_at.split('T')[0];
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    });

    // Fill in missing days with 0
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStr = date.toISOString().split('T')[0];
      result.push({
        date: dayStr,
        views: dayCounts[dayStr] || 0
      });
    }

    return result;
  } catch (error) {
    console.error('Error fetching views by day:', error);
    return [];
  }
};

export const getLiveVisitors = async () => {
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

  try {
    const { data, error } = await supabase
      .from('page_views')
      .select('visitor_id, page, city, country, device_type, created_at')
      .gte('created_at', fiveMinutesAgo)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get unique visitors with their most recent page
    const visitorMap = new Map();
    data?.forEach(view => {
      if (!visitorMap.has(view.visitor_id)) {
        visitorMap.set(view.visitor_id, view);
      }
    });

    return Array.from(visitorMap.values());
  } catch (error) {
    console.error('Error fetching live visitors:', error);
    return [];
  }
};
